<template>
  <div>
    <v-row class="ma-0 mb-5" align="start">
      <v-col cols="auto" class="pa-0">
        <h2 class="text-h6 mb-1">{{ 'smartQuoteSenderMappingsTitle'|localize }}</h2>
        <div class="text-body-2 grey--text">{{ 'smartQuoteSenderMappingsSubtitle'|localize }}</div>
      </v-col>
    </v-row>

    <v-card outlined class="mb-5 pa-4 smart-quote-sender-settings">
      <v-row dense align="start">
        <v-col cols="12" md="6">
          <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteAcceptedDomains'|localize }}</h6>
          <v-text-field
            v-model.trim="settingsForm.acceptedDomains"
            outlined
            dense
            :disabled="settingsLoading || settingsSaving"
            :placeholder="'smartQuoteAcceptedDomainsPlaceholder'|localize"
            :hint="'smartQuoteAcceptedDomainsHint'|localize"
            :error-messages="settingsErrors.accepted_domains"
            persistent-hint
          />
        </v-col>
        <v-col cols="12" md="4">
          <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteDefaultSender'|localize }}</h6>
          <v-select
            v-model="settingsForm.defaultSenderId"
            :items="users"
            item-text="name"
            item-value="id"
            outlined
            dense
            clearable
            :loading="usersLoading"
            :disabled="settingsLoading || settingsSaving"
            :placeholder="'smartQuoteDefaultSenderPlaceholder'|localize"
            :hint="'smartQuoteDefaultSenderHint'|localize"
            :error-messages="settingsErrors.default_sender_id"
            persistent-hint
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item.name }} ({{ item.email }})</v-list-item-title>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="2" class="d-flex align-start">
          <v-btn
            color="primary"
            depressed
            rounded
            block
            class="mt-8"
            :loading="settingsSaving"
            :disabled="settingsLoading"
            @click="saveSettings"
          >
            {{ 'save'|localize }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-row class="ma-0 mb-4" dense align="center">
      <v-col cols="12" sm="4">
        <v-text-field
          v-model.trim="searchEmail"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="'smartQuoteSenderSearchPlaceholder'|localize"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-select v-model="userFilter" :items="userFilterOptions" dense outlined hide-details />
      </v-col>
      <v-col cols="12" sm="2">
        <v-select v-model="statusFilter" :items="statusFilterOptions" dense outlined hide-details />
      </v-col>
      <v-col cols="12" sm="3" class="d-flex justify-end">
        <v-btn color="primary" depressed rounded class="text-no-wrap" @click="openAddDrawer">
          <v-icon size="20" left>mdi-plus</v-icon>
          {{ 'smartQuoteAddSender'|localize }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      class="smart-quote-sender-table"
      :headers="headers"
      :items="filteredSenders"
      :loading="loading"
      loader-height="2"
      :items-per-page="-1"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :no-data-text="'noDataAvailable'|localize"
      hide-default-footer
    >
      <template v-slot:item.senderEmail="{ item }">
        <v-tooltip top color="black" max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="d-block smart-quote-sender-cell-ellipsis">{{ item.senderEmail }}</span>
          </template>
          <span>{{ item.senderEmail }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.user="{ item }">
        <span v-if="item.userName" class="d-block smart-quote-sender-cell-ellipsis">{{ item.userName }}</span>
        <span v-else class="font-italic">{{ 'smartQuoteSenderCompanyLevel'|localize }}</span>
      </template>
      <template v-slot:item.source="{ item }">
        {{ sourceLabel(item.source) }}
      </template>
      <template v-slot:item.lastUsedAt="{ item }">
        {{ item.lastUsedAt ? formatDateTime(item.lastUsedAt) : '—' }}
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
      <template v-slot:item.enabled="{ item }">
        <v-chip small :color="item.enabled ? 'success' : 'grey lighten-1'" text-color="white">
          {{ (item.enabled ? 'smartQuoteStatusActive' : 'smartQuoteStatusDisabled')|localize }}
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-end smart-quote-sender-actions">
          <v-btn text small color="primary" @click="openEditDrawer(item)">
            <v-icon left size="16">mdi-pencil</v-icon>{{ 'edit'|localize }}
          </v-btn>
          <v-btn
            text
            small
            :loading="!!togglingIds[item.id]"
            :disabled="!!togglingIds[item.id]"
            @click="toggleEnabled(item)"
          >
            <v-icon left size="16">{{ item.enabled ? 'mdi-cancel' : 'mdi-check' }}</v-icon>
            {{ (item.enabled ? 'smartQuoteDisable' : 'smartQuoteEnable')|localize }}
          </v-btn>
          <v-btn
            text
            small
            color="error"
            :loading="!!removingIds[item.id]"
            :disabled="!!removingIds[item.id]"
            @click="openRemoveDialog(item)"
          >
            <v-icon left size="16">mdi-delete</v-icon>{{ 'smartQuoteRemoveSender'|localize }}
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-navigation-drawer v-model="drawer" right temporary fixed width="400" class="smart-quote-sender-drawer">
      <div class="pa-5" v-if="drawer">
        <div class="d-flex align-center justify-space-between mb-4">
          <span class="text-h6">{{ (editingId ? 'smartQuoteEditSenderTitle' : 'smartQuoteAddSenderTitle')|localize }}</span>
          <v-btn icon @click="closeDrawer">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-form ref="senderForm" lazy-validation>
          <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteSenderEmail'|localize }} *</h6>
          <v-text-field
            v-model.trim="form.senderEmail"
            outlined
            dense
            :disabled="saving"
            :rules="[validationRules.required, validationRules.email]"
          />

          <h6 class="text-subtitle-2 mb-2">{{ 'company'|localize }}</h6>
          <v-text-field :value="company_name" outlined dense readonly disabled class="mb-2" />

          <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteSenderAssignUser'|localize }} *</h6>
          <v-select
            v-model="form.userId"
            :items="assignableUsers"
            item-text="name"
            item-value="id"
            outlined
            dense
            clearable
            :loading="usersLoading"
            :disabled="saving"
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item.name }} ({{ item.email }})</v-list-item-title>
            </template>
          </v-select>

          <h6 class="text-subtitle-2 mb-2">{{ 'status'|localize }}</h6>
          <v-switch
            v-model="form.enabled"
            :label="(form.enabled ? 'smartQuoteStatusActive' : 'smartQuoteStatusDisabled')|localize"
            :disabled="saving || !editingId"
            inset
            dense
            hide-details
            class="mt-0 mb-4 smart-quote-sender-switch"
          />

          <template v-if="editingId">
            <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteAuditSectionTitle'|localize }}</h6>
            <div class="d-flex align-center text-body-2 mb-2">
              <v-icon size="18" left :color="editingSource === 'smart_quote_review' ? 'purple' : 'grey'">
                {{ editingSource === 'smart_quote_review' ? 'mdi-creation' : 'mdi-pencil-outline' }}
              </v-icon>
              {{ (editingSource === 'smart_quote_review' ? 'smartQuoteAuditCreatedFromReview' : 'smartQuoteAuditCreatedManually')|localize }}
            </div>
            <div v-if="editingLastMessageRequestId" class="d-flex align-center text-body-2 mb-4">
              <v-icon size="18" left color="info">mdi-email-outline</v-icon>
              {{ 'smartQuoteAuditLastMessage'|localize }}: {{ editingLastMessageRequestId }}
            </div>
          </template>
        </v-form>

        <v-row class="mt-2">
          <v-col>
            <v-btn color="primary" block depressed rounded :loading="saving" @click="save">
              {{ 'smartQuoteSaveMapping'|localize }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn color="primary" block outlined rounded :disabled="saving" @click="closeDrawer">
              {{ 'cancel'|localize }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="removeDialog" width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ 'smartQuoteRemoveSender'|localize }}</v-card-title>
        <v-card-text class="text-body-2">
          <div>{{ 'smartQuoteRemoveSenderConfirm'|localize }}</div>
          <div v-if="removeItem" class="mt-2 grey--text">{{ removeItem.senderEmail }}</div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="isRemoving(removeItem)" @click="closeRemoveDialog">
            {{ 'cancel'|localize }}
          </v-btn>
          <v-btn color="error" depressed :loading="isRemoving(removeItem)" @click="confirmRemove">
            {{ 'smartQuoteRemoveSender'|localize }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import localize from '@/filters/localize';
  import validationRules from '../helpers/validationRules';
  import methods from '@/helpers/methods';
  import api from '@/api/api';

  export default {
    name: 'CompanySmartQuoteSenders',
    props: ['company_id', 'company_name'],

    data() {
      return {
        validationRules,
        loading: false,
        senders: [],
        togglingIds: {},
        removingIds: {},
        removeDialog: false,
        removeItem: null,
        users: [],
        usersLoading: false,

        searchEmail: '',
        userFilter: '',
        statusFilter: '',
        sortBy: 'lastUsedAt',
        sortDesc: true,

        settingsLoading: false,
        settingsSaving: false,
        settingsErrors: {},
        settingsForm: {
          acceptedDomains: '',
          defaultSenderId: null,
        },

        drawer: false,
        saving: false,
        editingId: null,
        editingSource: null,
        editingLastMessageRequestId: null,
        form: {
          senderEmail: '',
          userId: null,
          enabled: true,
        },
      };
    },

    mounted() {
      this.getSenders();
      this.getUsers();
      this.getSettings();
    },

    computed: {
      headers() {
        return [
          {text: localize('smartQuoteSenderEmail'), value: 'senderEmail', sortable: false},
          {text: localize('smartQuoteColumnAssignedUser'), value: 'user', sortable: false, width: 160},
          {text: localize('smartQuoteColumnSource'), value: 'source', sortable: false, width: 170},
          {text: localize('smartQuoteColumnLastUsed'), value: 'lastUsedAt', sortable: true, width: 170},
          {text: localize('smartQuoteSenderCreatedAt'), value: 'createdAt', sortable: false, width: 120},
          {text: localize('status'), value: 'enabled', sortable: false, align: 'center', width: 110},
          {text: '', value: 'actions', sortable: false, align: 'end', width: 310},
        ];
      },
      userFilterOptions() {
        return [
          {value: '', text: localize('smartQuoteFilterAllUsers')},
          ...this.users.map(user => ({value: user.id, text: user.name})),
        ];
      },
      statusFilterOptions() {
        return [
          {value: '', text: localize('smartQuoteFilterAll')},
          {value: 'active', text: localize('smartQuoteStatusActive')},
          {value: 'disabled', text: localize('smartQuoteStatusDisabled')},
        ];
      },
      filteredSenders() {
        let list = this.senders;

        if (this.searchEmail) {
          let needle = this.searchEmail.toLowerCase();
          list = list.filter(item => item.senderEmail && item.senderEmail.toLowerCase().includes(needle));
        }

        if (this.userFilter) {
          list = list.filter(item => item.userId === this.userFilter);
        }

        if (this.statusFilter === 'active') {
          list = list.filter(item => item.enabled);
        } else if (this.statusFilter === 'disabled') {
          list = list.filter(item => !item.enabled);
        }

        return list;
      },
      // A sender may only be pinned to a user who can actually take requests — i.e. one with a valid
      // relevancy for this company (has_relevancy from getCompanyUsers). The backend enforces the
      // same rule; this just keeps the picker from offering users it would reject.
      assignableUsers() {
        return this.users.filter(user => user.has_relevancy);
      },
    },

    methods: {
      sourceLabel(source) {
        return source === 'smart_quote_review' ? localize('smartQuoteSourceReview') : localize('smartQuoteSourceManual');
      },
      async getSenders() {
        try {
          this.loading = true;
          let response = await api.getCompanySmartQuoteSenders(this.company_id);
          this.senders = response.data.items;
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.loading = false;
        }
      },
      async getSettings() {
        try {
          this.settingsLoading = true;
          let response = await api.getCompanySmartQuoteSettings(this.company_id);
          this.applySettings(response.data);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.settingsLoading = false;
        }
      },
      applySettings(data) {
        this.settingsForm.acceptedDomains = (data.acceptedDomains || []).join(', ');
        this.settingsForm.defaultSenderId = data.defaultSenderId || null;
      },
      async saveSettings() {
        try {
          this.settingsSaving = true;
          this.settingsErrors = {};

          let response = await api.updateCompanySmartQuoteSettings(this.company_id, {
            accepted_domains: this.settingsForm.acceptedDomains || '',
            default_sender_id: this.settingsForm.defaultSenderId || null,
          });

          // Echo back the normalized list (lower-cased, de-duplicated, "@" stripped) so the admin
          // sees exactly what the matcher will compare against.
          this.applySettings(response.data);
          methods.showGlobalSnackbar('success', localize('smartQuoteSettingsSaved'));
        } catch (e) {
          this.settingsErrors = (e.response && e.response.data && e.response.data.errors) || {};
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.settingsSaving = false;
        }
      },
      async getUsers() {
        try {
          this.usersLoading = true;
          let usersData = await api.getCompanyUsers(this.company_id);
          this.users = usersData.data;
        } catch (e) {
          // Non-blocking — the sender form still works without the user picker.
        } finally {
          this.usersLoading = false;
        }
      },
      async toggleEnabled(item) {
        if (this.togglingIds[item.id]) return;
        this.$set(this.togglingIds, item.id, true);
        try {
          let response = await api.updateSmartQuoteSender(item.id, {enabled: !item.enabled});
          let index = this.senders.findIndex(sender => sender.id === item.id);
          if (index !== -1) this.$set(this.senders, index, response.data);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.$delete(this.togglingIds, item.id);
        }
      },
      isRemoving(item) {
        return !!(item && this.removingIds[item.id]);
      },
      openRemoveDialog(item) {
        this.removeItem = item;
        this.removeDialog = true;
      },
      closeRemoveDialog() {
        this.removeDialog = false;
        this.removeItem = null;
      },
      async confirmRemove() {
        let item = this.removeItem;
        if (!item || this.removingIds[item.id]) return;
        this.$set(this.removingIds, item.id, true);
        try {
          await api.deleteSmartQuoteSender(item.id);
          let index = this.senders.findIndex(sender => sender.id === item.id);
          if (index !== -1) this.senders.splice(index, 1);
          this.closeRemoveDialog();
          methods.showGlobalSnackbar('success', localize('done'));
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.$delete(this.removingIds, item.id);
        }
      },
      openAddDrawer() {
        this.editingId = null;
        this.editingSource = null;
        this.editingLastMessageRequestId = null;
        this.form = {senderEmail: '', userId: null, enabled: true};
        this.drawer = true;
        this.$nextTick(() => this.$refs.senderForm && this.$refs.senderForm.resetValidation());
      },
      openEditDrawer(item) {
        this.editingId = item.id;
        this.editingSource = item.source;
        this.editingLastMessageRequestId = item.lastMessageRequestId;
        this.form = {senderEmail: item.senderEmail, userId: item.userId, enabled: item.enabled};
        this.drawer = true;
        this.$nextTick(() => this.$refs.senderForm && this.$refs.senderForm.resetValidation());
      },
      closeDrawer() {
        this.drawer = false;
      },
      async save() {
        if (!this.$refs.senderForm.validate()) return;
        try {
          this.saving = true;
          let response;
          if (this.editingId) {
            let payload = {
              sender_email: this.form.senderEmail,
              user_id: this.form.userId || null,
              enabled: this.form.enabled,
            };
            response = await api.updateSmartQuoteSender(this.editingId, payload);
            let index = this.senders.findIndex(sender => sender.id === this.editingId);
            if (index !== -1) this.$set(this.senders, index, response.data);
          } else {
            let payload = {sender_email: this.form.senderEmail};
            if (this.form.userId) payload.user_id = this.form.userId;
            response = await api.createCompanySmartQuoteSender(this.company_id, payload);
            // The backend upserts on the globally-unique sender_email, so "create" may return a row
            // that is already in the table (same id) — replace it in place instead of unshifting a
            // duplicate line for the same sender.
            let index = this.senders.findIndex(sender => sender.id === response.data.id);
            if (index !== -1) this.$set(this.senders, index, response.data);
            else this.senders.unshift(response.data);
          }
          this.saving = false;
          this.drawer = false;
          methods.showGlobalSnackbar('success', localize('done'));
        } catch (e) {
          this.saving = false;
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        }
      },
      formatDate(iso) {
        if (!iso) return '—';
        return methods.formatDate(new Date(iso));
      },
      formatDateTime(iso) {
        if (!iso) return '—';
        let date = new Date(iso);
        return `${methods.formatDate(date)} ${date.toTimeString().slice(0, 5)}`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .smart-quote-sender-actions {
    gap: 4px;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .smart-quote-sender-cell-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .smart-quote-sender-table table {
    table-layout: fixed;
  }

  ::v-deep .smart-quote-sender-table td {
    overflow: hidden;
  }

  ::v-deep .smart-quote-sender-drawer .v-navigation-drawer__content {
    overflow-x: hidden;
  }

  // The drawer's own label carries the state ("Active" / "Disabled"), so it reads as text next to
  // the control rather than as a form label: same size and weight as the table's status chip.
  ::v-deep .smart-quote-sender-switch .v-label {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
  }
</style>
